
import React from 'react';
import { 
  AppBar, Toolbar, Typography, IconButton, Drawer, List, ListItem, 
  ListItemIcon, ListItemText, CssBaseline, Box, Avatar, Divider,
  Badge, Menu, MenuItem
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HotelIcon from '@mui/icons-material/Hotel';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import InventoryIcon from '@mui/icons-material/Inventory';
import BarChartIcon from '@mui/icons-material/BarChart';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import BuildIcon from '@mui/icons-material/Build';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import { Link, useLocation } from 'react-router-dom';

const drawerWidth = 260;

const getNavItems = (userRole) => {
  // Customer - No navigation (single dashboard view)
  if (userRole === 'Customer') {
    return [];
  }
  
  // Staff - Limited access
  if (userRole === 'Staff') {
    return [
      { text: 'My Dashboard', icon: <DashboardIcon />, path: '/' },
      { text: 'Housekeeping', icon: <CleaningServicesIcon />, path: '/housekeeping' },
      { text: 'Services', icon: <RoomServiceIcon />, path: '/services' },
    ];
  }
  
  // Admin & Manager - Full access
  return [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Bookings', icon: <BookOnlineIcon />, path: '/bookings' },
    { text: 'Rooms', icon: <HotelIcon />, path: '/rooms' },
    { text: 'Customers', icon: <PeopleIcon />, path: '/customers' },
    { text: 'Staff', icon: <AssignmentIndIcon />, path: '/staff' },
    { text: 'Services', icon: <RoomServiceIcon />, path: '/services' },
    { text: 'Housekeeping', icon: <CleaningServicesIcon />, path: '/housekeeping' },
    { text: 'Inventory', icon: <InventoryIcon />, path: '/inventory' },
    { text: 'Maintenance', icon: <BuildIcon />, path: '/maintenance' },
    { text: 'Reports', icon: <BarChartIcon />, path: '/reports' },
    { text: 'User Management', icon: <ManageAccountsIcon />, path: '/users' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  ];
};

export default function Layout({ children, userRole = 'Admin', onLogout }) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const location = useLocation();
  const navItems = getNavItems(userRole);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#1a237e' }}>
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ bgcolor: '#fff', color: '#1a237e', width: 40, height: 40 }}>
          <HotelIcon />
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight="bold" sx={{ color: '#fff' }}>
            HotelOpsPro
          </Typography>
          <Typography variant="caption" sx={{ color: '#ffffffb3' }}>
            {userRole} Portal
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ bgcolor: '#ffffff30' }} />
      <List sx={{ px: 2, py: 2, flexGrow: 1 }}>
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem 
              button 
              key={item.text} 
              component={Link} 
              to={item.path}
              onClick={() => setMobileOpen(false)}
              sx={{
                mb: 0.5,
                borderRadius: 2,
                color: isActive ? '#fff' : '#ffffffb3',
                bgcolor: isActive ? '#303f9f' : 'transparent',
                '&:hover': {
                  bgcolor: isActive ? '#303f9f' : '#ffffff15',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItem>
          );
        })}
      </List>
      <Divider sx={{ bgcolor: '#ffffff30' }} />
      <Box sx={{ p: 2 }}>
        <Box sx={{ 
          p: 2, 
          bgcolor: '#303f9f', 
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: '#1976d2' }}>
            {userRole === 'Admin' ? 'A' : userRole === 'Manager' ? 'M' : userRole === 'Staff' ? 'S' : 'C'}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold' }}>
              {userRole} User
            </Typography>
            <Typography variant="caption" sx={{ color: '#ffffffb3' }}>
              {userRole.toLowerCase()}@hotelops.com
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar 
        position="fixed" 
        sx={{ 
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: '#fff',
          color: '#000',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {navItems.find(item => item.path === location.pathname)?.text || (userRole === 'Customer' ? 'My Account' : 'Dashboard')}
          </Typography>
          <IconButton color="inherit" component={Link} to="/notifications">
            <Badge badgeContent={4} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <IconButton color="inherit" component={Link} to="/settings">
            <SettingsIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleProfileMenuOpen}>
            <AccountCircleIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
          >
            <MenuItem onClick={handleProfileMenuClose}>Profile</MenuItem>
            <MenuItem onClick={handleProfileMenuClose}>Settings</MenuItem>
            <MenuItem onClick={() => {
              handleProfileMenuClose();
              onLogout && onLogout();
            }}>
              <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          bgcolor: '#f5f7fa',
          minHeight: '100vh',
          mt: 8,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
